import type { NextPage } from 'next';
import { useMemo } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getPageListData } from '@src/config/getPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { ListKeys } from '@src/constants/microcredits';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { useSelectUser } from '@src/reducers/user';
import { Layout as AppLayout } from 'app/Layout';
import { EbsBanner } from 'entities/Banner/ui/EbsBanner/EsbBanner';
import { fullDealOfferAction, sravniOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider/CardActionProvider';
import { SeoComponents } from 'entities/Seo/ui/SeoComponents';
import ProductList from 'features/ProductList/ui/ProductList/ProductList';
import { AnchorLinkList } from 'shared/ui';
import { Container } from 'shared/ui/Container/Container';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { LightweightCard } from 'widgets/LightweightCard';

import { getFilterMode, useGetHeaderFilters } from '../lib';

import styles from './ListPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.LIST,
  source: 'search',
  sortPanelVisible: true,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
  isMainPage: true,
  withCardDetailsClick: true,
};

export const ListPage: NextPage = () => {
  const isMobile = useIsMobile();
  const { filter, seo, pathname, hasUtmLabel, isMainMicroCreditsPage } = useListPage(ListKeys.LIST);
  const user = useSelectUser();
  const config = useMemo(
    () => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile, withCardDetailsClick: !hasUtmLabel }),
    [isMobile, hasUtmLabel],
  );

  useBestProducts();

  const { Filters } = useGetHeaderFilters(filter, seo, user);

  return (
    <AppLayout>
      <PageContext.Provider value={config}>
        <BrandingHeaders containerClassName={styles.page_header} afterHeaders={Filters} />
        <Container>
          {isMainMicroCreditsPage && <EbsBanner />}
          {!hasUtmLabel && <AnchorLinkList />}

          <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
            <ProductList
              className={styles.product_list}
              gridLayout
              filter={filter}
              pathname={pathname}
              renderCard={(props) => <LightweightCard hasUtmLabel={hasUtmLabel} {...props} />}
            />
          </CardActionProvider>
          {seo && (
            <SeoComponents
              seo={seo}
              breadcrumbs={seo.breadcrumbs}
              prefooter={seo.prefooter}
              showAnchorLinks={false}
              showSubheader={false}
            />
          )}
        </Container>

        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE, BottomBannerTypes.SIDEBAR]} />
      </PageContext.Provider>
    </AppLayout>
  );
};

ListPage.getInitialProps = (ctx: Application.ReduxNextPageContext) => {
  const filterMode = getFilterMode(ctx);

  return getPageListData(ctx, {
    key: ListKeys.LIST,
    defaultFiltersMode: filterMode,
  });
};
